// An email link's verifier, as the page keeps it. Asking for a link, the page
// makes a verifier (32 random bytes, in base64url) and sends only its
// challenge, the SHA-256 of its ASCII bytes in base64url, as PKCE's S256
// does (RFC 7636). The mail carries the attempt and a token; finishing sends
// both with the verifier, so a link opened anywhere else than where it was
// asked for signs no one in.
//
// The verifier is kept in localStorage, by attempt, with its expiry: a link
// opens in a new tab of the browser, which shares localStorage and not
// sessionStorage, and in the Mac app the page may have loaded again before
// the shell hands the link over. It is removed once used, and once expired,
// whenever another is kept or taken. A link's token never reaches storage.

/** Where each verifier is kept, by attempt. */
const PREFIX = 'poulpe.email-link.'

/** What is kept for one attempt: the verifier, until when, and the page the person asked from. */
export interface KeptVerifier {
  verifier: string
  /** RFC 3339, as the server answered it. */
  expires_at: string
  /** The page's path when the link was asked for: where finishing it goes. */
  back: string | null
}

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** A new verifier: 32 random bytes, in 43 characters of base64url. */
export function newVerifier(): string {
  return base64url(globalThis.crypto.getRandomValues(new Uint8Array(32)))
}

/** The verifier's challenge: the SHA-256 of its ASCII bytes, in 43 characters of base64url. */
export async function challengeOf(verifier: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64url(new Uint8Array(digest))
}

function storage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function read(store: Storage, key: string): KeptVerifier | null {
  try {
    const kept = JSON.parse(store.getItem(key) ?? 'null') as Partial<KeptVerifier> | null
    if (typeof kept?.verifier !== 'string' || typeof kept.expires_at !== 'string') return null
    const back = typeof kept.back === 'string' && kept.back.startsWith('/') && !kept.back.startsWith('//') ? kept.back : null
    return { verifier: kept.verifier, expires_at: kept.expires_at, back }
  } catch {
    return null
  }
}

/** Removes every verifier that has expired, or that cannot be read. */
function prune(store: Storage, now: number): void {
  const keys: string[] = []
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i)
    if (key?.startsWith(PREFIX)) keys.push(key)
  }
  for (const key of keys) {
    const kept = read(store, key)
    if (!kept || !(Date.parse(kept.expires_at) > now)) store.removeItem(key)
  }
}

/** Keeps the verifier of `attempt` until it expires. Without storage, the link can only say it was asked for elsewhere. */
export function keepVerifier(attempt: string, kept: KeptVerifier, now = Date.now()): void {
  const store = storage()
  if (!store) return
  try {
    prune(store, now)
    store.setItem(PREFIX + attempt, JSON.stringify(kept))
  } catch {
    // Full or refused: as without storage.
  }
}

/** The live verifier of `attempt`, or null when none was kept here, or it expired. */
export function keptVerifier(attempt: string, now = Date.now()): KeptVerifier | null {
  const store = storage()
  if (!store) return null
  try {
    prune(store, now)
    return read(store, PREFIX + attempt)
  } catch {
    return null
  }
}

/** Forgets the verifier of `attempt`, once its link was used. */
export function forgetVerifier(attempt: string): void {
  try {
    storage()?.removeItem(PREFIX + attempt)
  } catch {
    // Nothing kept, nothing to forget.
  }
}

/**
 * Whether the server could take it for an address: today's rule, roughly
 * (people/email.rs, normalise_address), so that a slip is said before
 * anything is sent. The server's rule decides.
 */
export function looksLikeAddress(address: string): boolean {
  return address.length <= 254 && /^[!#$%&'*+/=?^_`{|}~0-9A-Za-z-]+(\.[!#$%&'*+/=?^_`{|}~0-9A-Za-z-]+)*@([0-9A-Za-z]([0-9A-Za-z-]*[0-9A-Za-z])?\.)+[A-Za-z][0-9A-Za-z-]*$/.test(address)
}

/** A link's token, from the address's fragment: what follows `#`, or null. */
export function tokenOf(hash: string): string | null {
  const token = hash.startsWith('#') ? hash.slice(1) : hash
  return token ? token : null
}

/** Takes the fragment out of the browser's address at once, keeping the router's own state. */
export function dropFragment(): void {
  if (!window.location.hash) return
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
}
